/**
 * @description
 * Este arquivo define as rotas (paths) do frontend da aplicação (vulgo os diferentes caminhos da URL).
 *
 * As rotas descritas no arquivo `src/utils/axios.ts` são referentes aos endpoints do backend,
 * que serão utilizados para a integração do front-end ao back-end, via repositories.
 */

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
  ADMIN_USER: '/admin-user',
  CONVERSION_RATE: '/conversionrate',
  RULES: '/rules',
  VARIABLES: '/variables',
  INVOICES: '/invoices',
  ADMIN_LOG: '/adminlogs',
  POINTS: '/points',
  ADMIN_USERS: '/admin-users',
  APP_USERS: '/app-users',
  WEB_APP_USERS: '/web-app-users',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  FAQ: '/faq',
  SHOPPING_SETTINGS: '/shopping-settings',
  USER_LEVELS: '/user-levels',
  ESTABLISHMENTS: '/establishments',
  GIVEAWAYS: '/giveaways',
  CAMPAIGNS: '/campaigns',
  BENEFITS: '/benefits',
  EVENTS: '/events',
  COUPONS: '/coupons',
  PRODUCTS: '/products',
  REDEEMS: '/redeems',
  KEEP_UP: '/keep-up',
  HIGHLIGHTS: '/highlights',
  STORIES: '/stories',
  BANNERS: '/banners',
  HOME: '/home',
  NOTIFICATIONS: '/notifications',
};

export const paths = {
  root: '/',
  auth: {
    root: ROOTS.AUTH,
    login: `${ROOTS.AUTH}/login`,
    register: `${ROOTS.AUTH}/register`,
    refresh: `${ROOTS.AUTH}/refresh`,
  },

  error: {
    notFound: '/404',
    forbidden: '/403',
    serverError: '/500',
  },

  // PAGES
  dashboard: {
    root: ROOTS.DASHBOARD,

    // GERAÇÃO DE PONTOS
    rules: {
      root: `${ROOTS.DASHBOARD}${ROOTS.RULES}`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.RULES}/:id`,
      create: `${ROOTS.DASHBOARD}${ROOTS.RULES}/create`,
    },
    conversionRate: {
      root: `${ROOTS.DASHBOARD}${ROOTS.CONVERSION_RATE}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.CONVERSION_RATE}/create`,
    },
    variables: `${ROOTS.DASHBOARD}${ROOTS.VARIABLES}`,
    invoices: {
      root: `${ROOTS.DASHBOARD}${ROOTS.INVOICES}`,
      review: `${ROOTS.DASHBOARD}${ROOTS.INVOICES}/:id`,
    },

    // SORTEIOS
    giveaways: {
      root: `${ROOTS.DASHBOARD}${ROOTS.GIVEAWAYS}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.GIVEAWAYS}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.GIVEAWAYS}/:id`,
    },
    campaigns: {
      root: `${ROOTS.DASHBOARD}${ROOTS.CAMPAIGNS}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.CAMPAIGNS}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.CAMPAIGNS}/:id`,
    },

    // USUÁRIOS ADMINISTRADORES
    adminUsers: {
      root: `${ROOTS.DASHBOARD}${ROOTS.ADMIN_USER}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.ADMIN_USER}/create`,
      update: `${ROOTS.DASHBOARD}${ROOTS.ADMIN_USER}/update/:id`,
      inactivate: `${ROOTS.DASHBOARD}${ROOTS.ADMIN_USER}/inactivate/:id`,
    },
    adminLogs: `${ROOTS.DASHBOARD}${ROOTS.ADMIN_LOG}`,

    // USUÁRIOOS DO APP
    appUsers: {
      root: `${ROOTS.DASHBOARD}${ROOTS.APP_USERS}`,
      details: `${ROOTS.DASHBOARD}${ROOTS.APP_USERS}/:id`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.APP_USERS}/:id/edit`,
    },

    // USUÁRIOS DO WEB APP
    webAppUsers: {
      root: `${ROOTS.DASHBOARD}${ROOTS.WEB_APP_USERS}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.WEB_APP_USERS}/create`,
      update: `${ROOTS.DASHBOARD}${ROOTS.WEB_APP_USERS}/update/:id`,
    },

    // ESTABELECIMENTOS
    establishments: {
      root: `${ROOTS.DASHBOARD}${ROOTS.ESTABLISHMENTS}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.ESTABLISHMENTS}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.ESTABLISHMENTS}/:id/edit`,
      categories: `${ROOTS.DASHBOARD}${ROOTS.ESTABLISHMENTS}/categories`,
      createCategory: `${ROOTS.DASHBOARD}${ROOTS.ESTABLISHMENTS}/categories/create`,
      editCategory: `${ROOTS.DASHBOARD}${ROOTS.ESTABLISHMENTS}/categories/:id`,
    },

    // GESTÃO DE BENEFÍCIOS
    benefits: {
      // Rota genérica para listar todos os benefícios (vista de Admin)
      root: `${ROOTS.DASHBOARD}${ROOTS.BENEFITS}`,
    },
    events: {
      root: `${ROOTS.DASHBOARD}${ROOTS.EVENTS}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.EVENTS}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.EVENTS}/:id`,
    },
    coupons: {
      root: `${ROOTS.DASHBOARD}${ROOTS.COUPONS}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.COUPONS}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.COUPONS}/:id`,
    },
    products: {
      root: `${ROOTS.DASHBOARD}${ROOTS.PRODUCTS}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.PRODUCTS}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.PRODUCTS}/:id`,
    },
    redeems: {
      root: `${ROOTS.DASHBOARD}${ROOTS.REDEEMS}`,
    },

    // DOCUMENTOS
    terms: {
      root: `${ROOTS.DASHBOARD}${ROOTS.TERMS}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.TERMS}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.TERMS}/:id`,
      duplicate: `${ROOTS.DASHBOARD}${ROOTS.TERMS}/duplicate`,
    },
    privacy: {
      root: `${ROOTS.DASHBOARD}${ROOTS.PRIVACY}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.PRIVACY}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.PRIVACY}/:id`,
      duplicate: `${ROOTS.DASHBOARD}${ROOTS.PRIVACY}/duplicate`,
    },
    faq: {
      root: `${ROOTS.DASHBOARD}${ROOTS.FAQ}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.FAQ}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.FAQ}/:id`,
      categories: `${ROOTS.DASHBOARD}${ROOTS.FAQ}/categories`,
      createCategory: `${ROOTS.DASHBOARD}${ROOTS.FAQ}/categories/create`,
      editCategory: `${ROOTS.DASHBOARD}${ROOTS.FAQ}/categories/:id`,
    },
    // CONFIGURAÇÕES DO SHOPPING
    shoppingSettings: {
      root: `${ROOTS.DASHBOARD}${ROOTS.SHOPPING_SETTINGS}`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.SHOPPING_SETTINGS}/edit`,
    },

    // NÍVEIS DE USUÁRIO
    userLevels: {
      root: `${ROOTS.DASHBOARD}${ROOTS.USER_LEVELS}`,
    },

    // FIQUE POR DENTRO
    keepUp: {
      root: `${ROOTS.DASHBOARD}${ROOTS.KEEP_UP}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.KEEP_UP}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.KEEP_UP}/:id`,
    },

    // DESTAQUES
    highlights: {
      root: `${ROOTS.DASHBOARD}${ROOTS.HIGHLIGHTS}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.HIGHLIGHTS}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.HIGHLIGHTS}/:id`,
    },

    // STORIES
    stories: {
      root: `${ROOTS.DASHBOARD}${ROOTS.STORIES}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.STORIES}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.STORIES}/:id`,
    },

    // BANNERS
    banners: {
      root: `${ROOTS.DASHBOARD}${ROOTS.BANNERS}`,
    },

    // HOME
    home: {
      root: `${ROOTS.DASHBOARD}${ROOTS.HOME}`,
      create: `${ROOTS.DASHBOARD}${ROOTS.HOME}/create`,
      edit: `${ROOTS.DASHBOARD}${ROOTS.HOME}/:id`,
    },

    // NOTIFICAÇÕES
    notifications: {
      root: `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}`,
      campaigns: `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/`,
      createCampaign: `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/create`,
      editCampaign: (id: string) => `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/${id}/edit`,
      proximity: `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/proximity`,
      createProximity: `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/proximity/create`,
      editProximity: (id: string) => `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/proximity/${id}/edit`,
      segments: `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/segments`,
      createSegment: `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/segments/create`,
      editSegment: (id: string) => `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/segments/${id}`,
      logs: `${ROOTS.DASHBOARD}${ROOTS.NOTIFICATIONS}/logs`,
    },
  },
};
